import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetUserByStaffPass } from "./useGetUserByStaffPass";
import { TaskCategory, type ShiftTask } from "@/logic/api";

const TIMEOUT_MS = 30_000;
const ERROR_DISPLAY_MS = 2_000;
const SUCCESS_DISPLAY_MS = 1_500;

const STAFF_PASS_PATTERN = /^USR-[a-zA-Z0-9]+$/i;

interface ScannedUser {
  uuid: string;
  name: string;
  shiftUuid: string | null;
}

export type ScannerState =
  | { status: "idle" }
  | { status: "validating" }
  | { status: "waiting"; user: ScannedUser }
  | { status: "processing"; user: ScannedUser }
  | { status: "success"; user: ScannedUser }
  | { status: "error"; message: string };

interface UseDriverScannerProps {
  onStatusChange: (uuid: string, status: "on-shift" | "on-break" | "off-shift") => Promise<void>;
  onTaskChange: (uuid: string, task: string, shiftUuid?: string) => Promise<void>;
  tasks: ShiftTask[];
}

interface UseDriverScannerResult {
  state: ScannerState;
  timeoutSeconds: number;
  handleScan: (scannedValue: string) => void;
  resetScanner: () => void;
  handleCloseModal: () => void;
}

const isValidStaffPass = (value: string) =>
  STAFF_PASS_PATTERN.test(value);

const findTaskByKey = (key: string, tasks: ShiftTask[]) =>
  tasks.find(task => task.key === key);

const isStartShiftTask = (task: ShiftTask) =>
  task.category === TaskCategory.On && task.title.toLowerCase().includes("shift");

export const useDriverScanner = ({
  onStatusChange,
  onTaskChange,
  tasks,
}: UseDriverScannerProps): UseDriverScannerResult => {
  const { t } = useTranslation();
  const getUserByStaffPass = useGetUserByStaffPass();

  const [state, setState] = useState<ScannerState>({ status: "idle" });
  const [timeoutSeconds, setTimeoutSeconds] = useState(Math.floor(TIMEOUT_MS / 1000));

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const resetScanner = useCallback(() => {
    clearTimers();
    setState({ status: "idle" });
    setTimeoutSeconds(Math.floor(TIMEOUT_MS / 1000));
  }, [clearTimers]);

  const showErrorThenReset = useCallback((message: string) => {
    clearTimers();
    setState({ status: "error", message });
    setTimeout(resetScanner, ERROR_DISPLAY_MS);
  }, [clearTimers, resetScanner]);

  const startCountdown = useCallback(() => {
    clearTimers();
    setTimeoutSeconds(Math.floor(TIMEOUT_MS / 1000));

    timeoutRef.current = setTimeout(() => {
      showErrorThenReset(t("driverShiftTracker.scanner.timeout"));
    }, TIMEOUT_MS);

    countdownRef.current = setInterval(() => {
      setTimeoutSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  }, [clearTimers, showErrorThenReset, t]);

  const handleStaffPassScan = useCallback(async (scannedValue: string) => {
    if (!isValidStaffPass(scannedValue)) {
      showErrorThenReset(t("driverShiftTracker.scanner.invalidStaffPass"));
      return;
    }

    setState({ status: "validating" });

    try {
      const user = await getUserByStaffPass.mutateAsync({ staffPass: scannedValue });

      setState({
        status: "waiting",
        user: {
          uuid: user.uuid,
          name: `${user.firstName} ${user.lastName}`,
          shiftUuid: user.currentShift?.uuid ?? null,
        },
      });
      startCountdown();
    } catch {
      showErrorThenReset(t("driverShiftTracker.scanner.staffPassNotFound"));
    }
  }, [getUserByStaffPass, t, showErrorThenReset, startCountdown]);

  const handleActionScan = useCallback(async (scannedValue: string) => {
    if (state.status !== "waiting") return;

    const task = findTaskByKey(scannedValue, tasks);
    if (!task) {
      showErrorThenReset(t("driverShiftTracker.scanner.invalidAction"));
      return;
    }

    const { user } = state;

    if (!isStartShiftTask(task) && !user.shiftUuid) {
      showErrorThenReset(t("driverShiftTracker.scanner.noActiveShift"));
      return;
    }

    clearTimers();
    setState({ status: "processing", user });

    try {
      await processTaskAction(task, user, onStatusChange, onTaskChange);
      setState({ status: "success", user });
      setTimeout(resetScanner, SUCCESS_DISPLAY_MS);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      showErrorThenReset(message);
    }
  }, [state, tasks, clearTimers, onStatusChange, onTaskChange, t, showErrorThenReset, resetScanner]);

  const handleScan = useCallback((scannedValue: string) => {
    if (state.status === "idle" || state.status === "error") {
      handleStaffPassScan(scannedValue);
    } else if (state.status === "waiting") {
      handleActionScan(scannedValue);
    }
  }, [state.status, handleStaffPassScan, handleActionScan]);

  const handleCloseModal = useCallback(() => {
    if (state.status === "waiting" || state.status === "error") {
      resetScanner();
    }
  }, [state.status, resetScanner]);

  useEffect(() => clearTimers, [clearTimers]);

  return {
    state,
    timeoutSeconds,
    handleScan,
    resetScanner,
    handleCloseModal,
  };
};

async function processTaskAction(
  task: ShiftTask,
  user: ScannedUser,
  onStatusChange: UseDriverScannerProps["onStatusChange"],
  onTaskChange: UseDriverScannerProps["onTaskChange"]
): Promise<void> {
  const titleLower = task.title.toLowerCase();
  const isShiftAction = titleLower.includes("shift");
  const isBreakAction = titleLower.includes("break");

  switch (task.category) {
    case TaskCategory.On:
      if (isShiftAction) {
        await onStatusChange(user.uuid, "on-shift");
      } else if (isBreakAction) {
        await onStatusChange(user.uuid, "on-break");
      }
      break;

    case TaskCategory.Off:
      if (isShiftAction) {
        await onStatusChange(user.uuid, "off-shift");
      } else if (isBreakAction) {
        await onStatusChange(user.uuid, "on-shift");
      }
      break;

    case TaskCategory.Driving:
    case TaskCategory.NonDriving:
    default:
      await onTaskChange(user.uuid, task.key, user.shiftUuid ?? undefined);
      break;
  }
}
